import { BufferTrackerReact } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import { useCommonTrackingProperties } from './useCommonTrackingProperties'

/**
 * The post + channel fields forwarded into the `postDragged` tracking event.
 * All `postDragged` params are string-typed, so callers pass their own
 * (enum-typed) post/channel objects and this shape accepts the fields as plain
 * strings rather than coupling to a GraphQL entity type.
 */
type TrackPostDraggedPostType = {
  id: string
  dueAt?: string | null
  status: string
  channel: {
    id: string
    serviceId: string
    type: string
    displayName?: string | null
    name?: string | null
  }
}

type TrackPostDragged = (post: TrackPostDraggedPostType) => void

/**
 * A custom hook used for managing data required by "Post Dragged" tracking event.
 *
 * The hook returns a function, `trackPostDragged`, to track when a post is dragged using the `BufferTrackerReact.postDragged` method.
 *
 * @returns {function(TrackPostDraggedPostType): void} - A function to trigger the tracking of a post being dragged.
 *
 * @example
 * const trackPostDragged = usePostDraggedTracking();
 * trackPostDragged(post);
 */
export const usePostDraggedTracking = (): TrackPostDragged => {
  const organizationId = useOrganizationId()
  const commonTrackingProperties = useCommonTrackingProperties()

  const trackPostDragged: TrackPostDragged = (post) => {
    const { id: postId, channel, dueAt, status } = post
    const {
      id: channelId,
      serviceId: channelServiceId,
      type: channelType,
      displayName: channelDisplayName,
      name: channelName,
    } = channel

    BufferTrackerReact.postDragged({
      ...commonTrackingProperties,
      channelId,
      channelServiceId,
      channelType,
      channelUsername: channelDisplayName ?? channelName ?? '',
      clientName: 'publishWeb',
      dueAt: dueAt ?? '',
      organizationId: organizationId ?? '',
      postId,
      postStatus: status,
    })
  }

  return trackPostDragged
}
